import React, { useState, useEffect } from 'react';
import { Bike, ChevronDown, ChevronUp, Calendar, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { it, enGB } from 'date-fns/locale';
import config from '../config';
import { useNavigate, Link } from 'react-router-dom';

const BikeRentalList = ({ searchQuery = '', setActiveSidebarTab, setMapListView, setIsSidebarOpen }) => {
  const { t, i18n } = useTranslation();
  const preferredLanguage = i18n.language || 'en';
  const [bikes, setBikes] = useState([]);
  const [expandedBikeId, setExpandedBikeId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedDuration, setSelectedDuration] = useState('');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const currency = process.env.REACT_APP_CURRENCY || '€';
  const token = localStorage.getItem('token');

  const [takenSlots, setTakenSlots] = useState([]);

  // Set locale for datepicker
  useEffect(() => {
    if (i18n.language === 'it') {
      registerLocale('it', it);
    } else {
      registerLocale('en-GB', enGB);
    }
  }, [i18n.language]);

  // Fetch bikes from API
  useEffect(() => {
    const fetchBikes = async () => {
      try {
        const res = await fetch(config.getApiUrl('bikes'));
        const data = await res.json();
        if (data.success) {
          setBikes(data.bikes);
        }
      } catch (err) {
        console.error('Error fetching bikes:', err);
      }
    };
    fetchBikes();
  }, []);

  // Check bike availability
  useEffect(() => {
    if (expandedBikeId && selectedDate) {
      const selectedBike = bikes.find(b => b._id === expandedBikeId);
      if (!selectedBike) return;
  
      const formattedDate = selectedDate.toISOString().split('T')[0];
      fetch(`${config.getApiUrl(`bikes/availability/${selectedBike.bikeId}/${formattedDate}`)}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            console.log('🚲 Fetched takenSlots for', selectedBike.bikeId, 'on', formattedDate, ':', data.takenSlots);
            setTakenSlots(data.takenSlots || []);
          }
        })
        .catch(err => console.error('Error fetching availability:', err));
    }
  }, [expandedBikeId, selectedDate, bikes]); 
 
  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (token) {
        try {
          const res = await fetch(config.getApiUrl('users/me'), {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          const data = await res.json();
  
          if (res.ok && data.user) {
            //console.log('Fetched user:', data.user);
            //console.log('Fetched userLevel:', data.user.userLevel);
            setUser(data.user);
            localStorage.setItem('userData', JSON.stringify(data.user));
          } else {
            console.error('Error loading user profile');
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };
  
    fetchUserProfile();
  }, [token]);

  if (!user) {
    return (
      <div className="p-6 text-center text-gray-600">
          {t('bike.loginToView', 'Please log in to see bike rental options.')}

          <div>
              <button
              onClick={() => {
                  setActiveSidebarTab('profile');
                  setMapListView('list');
                  setIsSidebarOpen(true);
              }}
              className="mt-2 text-blue-600 underline text-sm"
              >
              {t('profile.goToProfile', 'Go to Profile')}
              </button>
          </div>
      </div>
    );
  }

  const filteredBikes = bikes.filter(bike => {
    const searchableText = (bike.description?.[preferredLanguage] + ' ' + bike.model).toLowerCase();
    return searchableText.includes(searchQuery.toLowerCase());
  });

  const calculateBasePrice = (bike, selectedDuration) => {
    if (!selectedDuration) return 0;
    if (selectedDuration === 'halfDay') return bike.base_price_half;
    if (selectedDuration === 'fullDay') return bike.base_price_full;
    if (selectedDuration.startsWith('hour') && bike.base_price_hour > 0) {
      const hours = parseInt(selectedDuration.split('r')[1]);
      return bike.base_price_hour * hours;
    }
    return 0;
  };

  const isDurationAvailable = (startTime, duration) => {
    if (!startTime) return false;
  
    const startHour = parseInt(startTime.split(':')[0]);
    let hoursNeeded = 0;
  
    if (duration === 'halfDay') hoursNeeded = 5;
    else if (duration === 'fullDay') hoursNeeded = 9;
    else if (duration.startsWith('hour')) {
      const n = parseInt(duration.replace('hour', ''));
      hoursNeeded = n + 1;
    }
  
    for (let i = 0; i < hoursNeeded; i++) {
      const slot = String(startHour + i).padStart(2, '0') + ':00';
      if (takenSlots.includes(slot)) return false;
    }
  
    return true;
  };

  const isBookingAllowed = user?.verified && user?.birthDate && user?.phoneNumber;

  return (
    <div className="space-y-4">
      {filteredBikes.map((bike) => {
        const isExpanded = expandedBikeId === bike._id;
        
        const handleExpand = (bikeId) => {
          if (expandedBikeId === bikeId) {
            // Collapse the bike card
            setExpandedBikeId(null);
            setSelectedDate(null);
            setSelectedTime('');
            setSelectedDuration('');
          } else {
            // Expand the bike card
            setExpandedBikeId(bikeId);
            setSelectedDate(new Date()); // 🗓 Today
            setSelectedTime('09:00');    // 🕘 9 AM
            //setSelectedDuration('half_day'); // Optional: Preselect "half day" rental
          }
        };        

        const isSupporter = user?.userLevel === 10;
        //console.log('Is Supporter:', isSupporter);
        console.log('User type:', user.userType);

        // Rental options per bike and user
        const rentalOptions = [
          { value: 'halfDay', label: t('bike.duration.halfDay', 'Half day (4h)') },
          { value: 'fullDay', label: t('bike.duration.fullDay', 'Full day (8h)') },
        ];

        if (bike.base_price_hour > 0 && (user.userType === 'resident' || user.userType === 'partner')) {
          rentalOptions.unshift(
            { value: 'hour1', label: '1h' },
            { value: 'hour2', label: '2h' },
            { value: 'hour3', label: '3h' }
          );
        }

        const basePrice = calculateBasePrice(bike, selectedDuration);
        const finalPrice = isSupporter ? Math.round(basePrice * 0.8) : Math.round(basePrice);

        return (
          <div
            key={bike._id}
            className="border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition relative"
          >
            {/* Header */}
            <div
              className="flex items-center justify-between mb-2 cursor-pointer"
              onClick={() => handleExpand(bike._id)}
            >
              <div className="flex items-center space-x-2">
                {bike.imageUrl ? (
                  <img
                    src={bike.imageUrl}
                    alt={bike.model}
                    className="h-10 w-10 rounded-full object-cover border border-gray-300"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <Bike className="h-5 w-5 text-green-500" />
                )}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{bike.model}</h3>
                  <p className="text-xs text-gray-500">{bike.bikeId}</p>
                </div>
              </div>
              {isExpanded ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </div>

            {/* Description and Specs */}
            <p className="text-sm text-gray-600 mb-2">{bike.description?.[preferredLanguage]}</p>
            <p className="text-xs text-gray-500 mb-2">{bike.specs?.[preferredLanguage]}</p>

            {/* Expanded booking section */}
            {isExpanded && (
              <div className="mt-4 text-center space-y-3">
                <p className="text-xs text-gray-500 mb-2">
                  {t('bike.selectDate', 'Select your rental date and time')}
                </p>

                {/* Date Picker */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-4 w-4 text-gray-400" />
                  </div>
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    minDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                    placeholderText={t('bike.selectDatePlaceholder', 'Select date')}
                    locale={i18n.language === 'it' ? 'it' : 'en-GB'}
                    className="w-full p-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* Time Picker */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Clock className="h-4 w-4 text-gray-400" />
                  </div>
                  <select
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="w-full p-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 appearance-none"
                  >
                    <option value="">{`-- ${t('bike.selectTime', 'Select time')} --`}</option>
                    {['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'].map((time) => (
                      <option key={time} value={time} disabled={takenSlots.includes(time)}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Rental Duration */}
                <div className="relative">
                  <select
                    value={selectedDuration}
                    onChange={(e) => setSelectedDuration(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 appearance-none"
                  >
                    {isExpanded && (
                      <pre className="text-xs text-gray-400 text-left">
                        takenSlots: {JSON.stringify(takenSlots)}
                      </pre>
                    )}
                    <option value="">{`-- ${t('bike.selectDuration', 'Select duration')} --`}</option>
                    {rentalOptions.map(option => (
                      <option
                        key={option.value}
                        value={option.value}
                        disabled={!isDurationAvailable(selectedTime, option.value)}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Price Display */}
                {selectedDuration && basePrice > 0 && (
                  <div className="mt-2">
                    {isSupporter ? (
                      <div className="flex items-center justify-center space-x-2">
                        <span className="text-gray-400 line-through text-lg">
                          {Math.round(basePrice)} {currency}
                        </span>
                        <span className="text-2xl font-bold text-green-600">
                          {finalPrice} {currency}
                        </span>
                      </div>
                    ) : (
                      <div className="text-2xl font-bold text-green-600">
                        {finalPrice} {currency}
                      </div>
                    )}
                  </div>
                )}

                {/* Book button */}
                {!isBookingAllowed && (
                  <p className="text-sm text-red-600 font-medium mb-2">
                    {t('profile.profileIncomplete', 'You need to verify your email and complete your profile before booking.')}
                    <br />
                    <Link to="/profile" className="underline text-blue-600">
                    {t('profile.goToProfile', 'Go to your profile')}
                </Link>

                  </p>
                )}

                <button
                  onClick={() => {
                    if (!selectedDate || !selectedTime || !selectedDuration) {
                      alert(t('bike.fillAllFields', 'Please select date, time and duration before booking.'));
                      return;
                    }

                    navigate('/bikerental/contract', {
                      state: {
                        bikeId: bike.bikeId,
                        bikeModel: bike.model,
                        rentalDate: selectedDate.toISOString().split('T')[0],
                        rentalTime: selectedTime,
                        rentalDuration: selectedDuration,
                        basePrice: basePrice,
                        finalPrice: finalPrice,
                      }
                    });
                  }}
                  className={`w-full py-2 rounded-md transition font-semibold ${
                    !isBookingAllowed
                      ? 'bg-gray-400 cursor-not-allowed text-white'
                      : 'bg-green-500 hover:bg-green-600 text-white'
                  }`}
                  disabled={!isBookingAllowed}
                >
                  {t('bike.bookNow', 'Book Now')}
                </button>

              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BikeRentalList;
